import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import CookieConsent from './components/CookieConsent';
import { LangProvider } from './contexts/LangContext';
import { stopAllAudio } from './lib/audioManager';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import CommunityPage from './pages/CommunityPage';
import ClassesPage from './pages/ClassesPage';
import ProfilePage from './pages/ProfilePage';
import TeacherDashboard from './pages/TeacherDashboard';
import TeacherBroadcastPage from './pages/TeacherBroadcastPage';
import PaymentSuccess from './pages/PaymentSuccess';
import BuddiesPage from './pages/BuddiesPage';
import AITeacherHub from './pages/AITeacherHub';
import ChallengePage from './pages/ChallengePage';
import ChallengesWrapper from './pages/ChallengesWrapper';
import PlacementTestPage from './pages/PlacementTestPage';
import WeeklyChallengePage from './pages/WeeklyChallengePage';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/PasswordResetPages';
import InviteSignupPage from './pages/InviteSignupPage';
import VideosPage from './pages/VideosPage';
import MaterialsPage from './pages/MaterialsPage';
import LernanalysePage from './pages/LernanalysePage';
import AlbumPage from './pages/AlbumPage';
import DudasPage, { DudaDetailPage } from './pages/DudasPage';
import TeacherReportPage from './pages/TeacherReportPage';
import VocabularyPage from './pages/VocabularyPage';
import ReadingPage from './pages/ReadingPage';
import LiveTranslatePage from './pages/LiveTranslatePage';
import ChangePasswordPage from './pages/ChangePasswordPage';

// Layout
import MainLayout from './components/layout/MainLayout';
import GlobalTranslator from './components/GlobalTranslator';
import MetaPixelManager from './components/MetaPixelManager';

const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#E06D53] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#737373]">Laden...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Forced password change (teacher-invite onboarding flow, and the Stripe
  // provisional-password flow): every route except /change-password itself
  // is blocked until the flag is cleared. This is a defense-in-depth layer —
  // AuthPage already shows an inline change-password form right after
  // login, but that only covers the synchronous case; this guard also
  // protects against a page refresh or a direct URL visit mid-flow.
  if (user.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  if (requiredRole && !requiredRole.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#E06D53] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRouter() {
  const location = useLocation();

  // Stop any playing audio when the route changes (prevents AI Teacher TTS
  // from continuing to speak after user navigates to another page)
  React.useEffect(() => {
    stopAllAudio();
  }, [location.pathname]);


  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<InviteSignupPage />} />
      <Route path="/placement-test" element={<PlacementTestPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Forced password change (teacher-invite onboarding). Renders raw —
          no MainLayout — because the user hasn't earned access to the app yet. */}
      <Route path="/change-password" element={
        <ProtectedRoute>
          <ChangePasswordPage />
        </ProtectedRoute>
      } />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <MainLayout><Dashboard /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/uben" element={
        <ProtectedRoute>
          <MainLayout><Dashboard only="uben" /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/kurs" element={
        <ProtectedRoute>
          <MainLayout><Dashboard only="kurs" /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/community" element={
        <ProtectedRoute>
          <MainLayout><CommunityPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/schreiben" element={
        <ProtectedRoute>
          <MainLayout><CommunityPage mode="writing" /></MainLayout>
        </ProtectedRoute>
      } />
      {/* Legacy routes — absorbed into /ai-teacher (Spiele hub). */}
      <Route path="/exercises" element={<Navigate to="/ai-teacher" replace />} />
      <Route path="/games" element={<Navigate to="/ai-teacher" replace />} />
      <Route path="/classes" element={
        <ProtectedRoute>
          <MainLayout><ClassesPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <MainLayout><ProfilePage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/buddies" element={
        <ProtectedRoute>
          <MainLayout><BuddiesPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/ai-teacher" element={
        <ProtectedRoute>
          <MainLayout><AITeacherHub /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/videos" element={
        <ProtectedRoute>
          <MainLayout><VideosPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/materials" element={
        <ProtectedRoute>
          <MainLayout><MaterialsPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/lernanalyse" element={
        <ProtectedRoute>
          <MainLayout><LernanalysePage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/aufnahmen" element={<Navigate to="/dashboard" replace />} />
      <Route path="/album" element={
        <ProtectedRoute>
          <MainLayout><AlbumPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/challenges" element={
        <ProtectedRoute>
          <MainLayout><ChallengesWrapper /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/weekly-challenge" element={
        <ProtectedRoute>
          <MainLayout><ChallengesWrapper /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/teacher" element={
        <ProtectedRoute requiredRole={['admin', 'teacher']}>
          <MainLayout><TeacherDashboard /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/teacher/push" element={
        <ProtectedRoute requiredRole={['admin', 'teacher']}>
          <MainLayout><TeacherBroadcastPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/teacher/users/:user_id/report" element={
        <ProtectedRoute requiredRole={['admin', 'teacher']}>
          <MainLayout><TeacherReportPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/dudas" element={
        <ProtectedRoute>
          <MainLayout><DudasPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/dudas/:question_id" element={
        <ProtectedRoute>
          <MainLayout><DudaDetailPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/vocabulary" element={
        <ProtectedRoute>
          <MainLayout><VocabularyPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/reading" element={
        <ProtectedRoute>
          <MainLayout><ReadingPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/pronunciation" element={<Navigate to="/materials?tile=speaking" replace />} />
      <Route path="/live-translate" element={
        <ProtectedRoute>
          <LiveTranslatePage />
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <LangProvider defaultLang="de">
            <AppRouter />
            <GlobalTranslator />
            <MetaPixelManager />
            <Toaster position="bottom-right" richColors duration={2500} toastOptions={{ style: { opacity: 0.88 } }} />
            <CookieConsent />
          </LangProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
